/**
 * ExportButton - 导出按钮组件
 * 提供 Deal 数据导出功能
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Download, FileJson, FileText, ChevronDown, 
  Check, Loader2, Shield, X, AlertTriangle
} from 'lucide-react'
import { 
  downloadDealExportWithAuth,
  ExportType, 
  ExportFormat,
  exportTypeLabels,
  exportTypeDescriptions,
  exportFormatLabels
} from '../services/export'
import { parseApiError } from '../services/api'

interface ExportButtonProps {
  dealId: string
  dealName: string
  className?: string
  compact?: boolean
}

export default function ExportButton({
  dealId,
  dealName,
  className = '',
  compact = false
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportType, setExportType] = useState<ExportType>('summary')
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  
  const handleExport = async () => {
    setIsExporting(true)
    setError(null)
    
    try {
      await downloadDealExportWithAuth(dealId, exportType, exportFormat)
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        setIsOpen(false)
      }, 1500)
    } catch (err) {
      const apiError = parseApiError(err)
      setError(apiError.message)
    } finally {
      setIsExporting(false)
    }
  }
  
  const exportTypeOptions: ExportType[] = ['summary', 'full', 'hypothesis', 'evidence']
  const exportFormatOptions: ExportFormat[] = ['markdown', 'json']
  
  return (
    <div className={`relative ${className}`}>
      {/* 触发按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors ${
          compact ? '' : 'border border-gray-200'
        }`}
      >
        <Download className="w-4 h-4" />
        {!compact && <span>导出</span>}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* 下拉面板 */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* 点击外部关闭 */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)} 
            />
            
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden"
            >
              {/* 头部 */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">导出数据</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{dealName}</p>
              </div>
              
              {/* 脱敏提示 */}
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
                <div className="flex items-center gap-2 text-amber-700">
                  <Shield className="w-4 h-4" />
                  <span className="text-xs">导出数据将自动脱敏，敏感信息已隐藏</span>
                </div>
              </div>
              
              <div className="p-4 space-y-4">
                {/* 导出类型 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    导出内容
                  </label>
                  <div className="space-y-1">
                    {exportTypeOptions.map((type) => (
                      <label
                        key={type}
                        className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          exportType === type
                            ? 'bg-primary-50 border border-primary-200'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <input
                          type="radio"
                          name="exportType"
                          value={type}
                          checked={exportType === type}
                          onChange={(e) => setExportType(e.target.value as ExportType)}
                          className="mt-0.5"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {exportTypeLabels[type]}
                          </div>
                          <div className="text-xs text-gray-500">
                            {exportTypeDescriptions[type]}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* 导出格式 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    文件格式
                  </label>
                  <div className="flex gap-2">
                    {exportFormatOptions.map((format) => (
                      <button
                        key={format}
                        onClick={() => setExportFormat(format)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                          exportFormat === format
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {format === 'markdown' ? (
                          <FileText className="w-4 h-4" />
                        ) : (
                          <FileJson className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {exportFormatLabels[format]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* 错误提示 */}
                {error && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-100 rounded-lg text-red-600">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-xs">{error}</span>
                  </div>
                )}
                
                {/* 导出按钮 */}
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-colors ${
                    success
                      ? 'bg-green-500 text-white'
                      : isExporting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {success ? (
                    <>
                      <Check className="w-5 h-5" />
                      导出成功
                    </>
                  ) : isExporting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      正在导出...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      开始导出
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

